import React, { useState } from 'react';

const BillingPage = () => {
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [isLoading, setIsLoading] = useState(false);

  const handleStripeCheckout = async (plan) => {
    setIsLoading(true);
    try {
      // Get user_id from localStorage
      const user_id = localStorage.getItem('user_id');
      
      if (!user_id) {
        alert('Please activate the service first by clicking "Activate Service" on the main page.');
        setIsLoading(false);
        return;
      }

      // Get API base URL from environment or use fallback
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.nuclearenergyagent.com';

      const response = await fetch(`${apiBaseUrl}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          plan: plan,
          user_id: user_id
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.detail || `HTTP ${response.status}`;
        throw new Error(errorMsg);
      }
      
      const data = await response.json();
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received from server');
      }
    } catch (error) {
      console.error('Stripe checkout error:', error);
      alert('Error processing payment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const subscriptionPlans = [
    {
      id: 'pro_subscription',
      name: 'Pro Subscription',
      price: '$4.99',
      period: '/mo',
      description: 'Ideal for regular users',
      features: [
        '50 AI-powered summaries each month',
        'All summary modes (General, Variables, Mixed)',
        'Standard processing speed',
        'Email support'
      ],
      buttonText: 'Select Plan',
      isRecommended: false
    },
    {
      id: 'lab_pro_unlimited',
      name: 'Lab Pro',
      price: '$49.99',
      period: '/mo',
      description: 'Everything in Pro',
      features: [
        'Unlimited AI-powered summaries',
        'All summary modes (General, Variables, Mixed)',
        'Standard processing speed',
        'Email support',
        'Designed for research teams and advanced userss',
      ],
      buttonText: 'Select Plan',
      isRecommended: true
    }
  ];

  const creditPacks = [
    {
      id: 'credit_pack_10',
      name: '10 Credits',
      price: '$1.99',
      description: 'Perfect for trying out the service',
      credits: '10 summaries'
    },
    {
      id: 'credit_pack_50',
      name: '50 Credits',
      price: '$5.99',
      description: 'Great for regular use',
      credits: '50 summaries'
    },
    {
      id: 'credit_pack_200',
      name: '200 Credits',
      price: '$14.99',
      description: 'Best value for power users',
      credits: '200 summaries'
    }
  ];

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 py-8 px-4 text-white">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-extrabold text-cyan-400 mb-4">
            Nuclear Energy Agent
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Subscriptions & Credits
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-12">
          <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-xl p-1">
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'subscriptions'
                  ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-500/25'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              Subscriptions
            </button>
            <button
              onClick={() => setActiveTab('credits')}
              className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'credits'
                  ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-500/25'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              Credits
            </button>
          </div>
        </div>

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <div className="space-y-8">
            <h2 className="text-3xl font-bold text-center text-white mb-8">
              Choose Your Subscription Plan
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {subscriptionPlans.map((plan) => (
                <div
                  key={plan.id}
                  className={`relative bg-gray-900/80 backdrop-blur-sm border rounded-2xl shadow-lg hover:shadow-blue-500/50 p-8 transition-all ${
                    plan.isRecommended
                      ? 'border-cyan-500 ring-2 ring-cyan-500/20'
                      : 'border-gray-700 hover:border-cyan-500'
                  }`}
                >
                  {plan.isRecommended && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-cyan-600 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg">
                        Recommended
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                    <p className="text-gray-400 mb-4">{plan.description}</p>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-extrabold text-cyan-400">{plan.price}</span>
                      <span className="text-gray-400 ml-1">{plan.period}</span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-gray-300">
                        <svg className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleStripeCheckout(plan.id)}
                    disabled={isLoading}
                    className={`w-full py-4 rounded-xl font-semibold text-lg transition-all ${
                      plan.isRecommended
                        ? 'bg-cyan-600 hover:bg-cyan-700 text-white shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isLoading ? 'Processing...' : plan.buttonText}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Credits Tab */}
        {activeTab === 'credits' && (
          <div className="space-y-8">
            <h2 className="text-3xl font-bold text-center text-white mb-8">
              Buy Credit Packs
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {creditPacks.map((pack) => (
                <div
                  key={pack.id}
                  className="bg-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-2xl shadow-lg hover:shadow-blue-500/50 p-6 transition-all hover:border-cyan-500"
                >
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-white mb-2">{pack.name}</h3>
                    <p className="text-gray-400 mb-4">{pack.description}</p>
                    <div className="text-3xl font-extrabold text-cyan-400 mb-2">{pack.price}</div>
                    <p className="text-sm text-gray-300">{pack.credits}</p>
                  </div>

                  <button
                    onClick={() => handleStripeCheckout(pack.id)}
                    disabled={isLoading}
                    className="w-full py-3 rounded-xl font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Processing...' : 'Buy Now'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer Info */}
        <div className="mt-16 text-center">
          <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-xl font-semibold text-white mb-4">Why Choose Nuclear Energy Agent?</h3>
            <div className="grid md:grid-cols-3 gap-6 text-gray-300">
              <div className="text-center">
                <div className="w-12 h-12 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h4 className="font-semibold text-white mb-2">Lightning Fast</h4>
                <p className="text-sm">Process complex scientific documents in seconds</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h4 className="font-semibold text-white mb-2">Accurate Results</h4>
                <p className="text-sm">AI-powered extraction of key variables and insights</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h4 className="font-semibold text-white mb-2">Secure & Private</h4>
                <p className="text-sm">Your documents are processed securely and never stored</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
